import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatErr } from "../lib/auth";
import { Shield, ArrowRight, Eye, EyeOff } from "lucide-react";

// Google "G" logo inline SVG
function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.5 16.3 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.2C41.9 35.5 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
  const redirectTo = safeNext || location.state?.from || "/dashboard";

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (isRegister) await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      navigate(redirectTo);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  };

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="section hero-radial min-h-[85vh] flex items-center">
      <div className="container-if max-w-md mx-auto">
        <div className="card-dark p-9">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-[var(--if-gold)]" strokeWidth={1.5} />
            <span className="font-mono-label text-[var(--if-gold)]">Client Portal</span>
          </div>
          <h1 className="font-display text-4xl mb-2">
            {isRegister ? "Create your account." : "Welcome back."}
          </h1>
          <p className="text-sm text-white/60 mb-6" style={{ fontFamily: "Inter, sans-serif" }}>
            {isRegister ? "Track your progress, chat with your AI strategist, and manage bookings." : "Sign in to access your dashboard."}
          </p>
          {!isRegister && new URLSearchParams(location.search).get("expired") && (
            <div className="mb-5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-700" style={{ fontFamily: "Inter, sans-serif" }} data-testid="session-expired-banner">
              Your session expired — please sign in again to continue where you left off.
            </div>
          )}

          <button onClick={continueWithGoogle} className="w-full inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-full bg-white text-[#1a1a17] font-semibold text-sm hover:bg-gray-100 transition-all mb-4" data-testid="auth-google">
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-xs text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>OR</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          <form onSubmit={submit} className="flex flex-col gap-3">
            {isRegister && (
              <input required className="if-input" placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" />
            )}
            <input required type="email" className="if-input" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
            <div className="relative">
              <input required type={showPassword ? "text" : "password"} className="if-input pr-11" placeholder="Password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
              <button type="button" onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                data-testid="auth-password-toggle">
                {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
              </button>
            </div>
            {err && <div className="text-red-400 text-sm" data-testid="auth-err">{err}</div>}
            <button type="submit" disabled={loading} className="btn-gold w-full mt-1" data-testid="auth-submit">
              {loading ? "…" : <>{isRegister ? "Create account" : "Sign in"} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {!isRegister && (
            <p className="text-sm text-center text-white/60 mt-4" style={{ fontFamily: "Inter, sans-serif" }}>
              <Link to="/forgot-password" className="text-[var(--if-gold)] underline" data-testid="auth-forgot">
                Forgot your password?
              </Link>
            </p>
          )}

          <div className="divider-gold my-6" />

          <p className="text-sm text-center text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>
            {isRegister ? "Already have an account?" : "New to Infantry?"}{" "}
            <Link to={isRegister ? "/login" : "/register"} className="text-[var(--if-gold)] underline" data-testid="auth-switch">
              {isRegister ? "Sign in" : "Create account"}
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
